import { Box } from '@mui/material';
import CloseIcon from 'remixicon-react/CloseFillIcon';

type ProactiveTeaserProps = {
  text: string;
  botName?: string;
  primaryColor?: string;
  onOpen: () => void;
  onDismiss: () => void;
};

// Launcher-hosted proactive teaser. It lives outside the iframe because the
// launcher hides the iframe while minimized, so a teaser rendered inside it
// could never be seen. Clicking opens the chat; the dismiss control delegates
// the real dismiss back to the iframe via the postMessage bridge.
//
// Styled to match the launcher's existing prompt bubble (same neutral surface,
// radius, shadow, position and sans-serif font) so it reads as part of the same
// widget; the bot-name label uses the tenant's primary color. The unread count
// is not rendered here — it lives on the chat toggle button (see App.tsx).
function ProactiveTeaser({ text, botName, primaryColor, onOpen, onDismiss }: ProactiveTeaserProps) {
  const accent = primaryColor || '#212529';
  const preview = text.length > 80 ? `${text.slice(0, 80)}…` : text;

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <Box
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label="Open chat to see the new message"
      sx={{
        fontFamily: 'sans-serif',
        position: 'absolute',
        right: 0,
        bottom: 69,
        maxWidth: '260px',
        minWidth: '200px',
        background: '#e8e8e8 !important',
        borderRadius: '6px',
        padding: '10px 32px 12px 20px',
        color: '#212529 !important',
        cursor: 'pointer',
        boxShadow:
          '0px 3px 5px -1px rgba(0,0,0,0.2),' +
          '0px 6px 10px 0px rgba(0,0,0,0.14),' +
          '0px 1px 18px 0px rgba(0,0,0,0.12)',
        pointerEvents: 'auto',
        '&:focus-visible': {
          outline: '2px solid #666',
          outlineOffset: '2px',
        },
      }}
    >
      <Box
        component="button"
        onClick={handleDismiss}
        aria-label="Dismiss message"
        sx={{
          position: 'absolute',
          top: '4px',
          right: '4px',
          width: '20px',
          height: '20px',
          p: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          '&:hover': { background: 'rgba(0,0,0,0.1)' },
        }}
      >
        <CloseIcon size={14} />
      </Box>
      {botName ? (
        <Box component="div" sx={{ fontWeight: 700, color: accent, fontSize: '16px', mb: '4px', pr: '14px' }}>
          {botName}
        </Box>
      ) : null}
      <Box component="div" sx={{ fontSize: '16px', lineHeight: 1.35, color: '#212529' }}>
        {preview}
      </Box>
    </Box>
  );
}

export default ProactiveTeaser;
